import { getScoreMetricsSpeechace, getScoreDataSpeechace, sendDataSpeechaceMetrics } from "../../../service/service";
import LogProcess from "../../StructuredLog/LogProcess";

import { getMetricsWithFallback } from '../../../helpers/progressFallback';

import { standardizeVRData, sendDataProgressTrackerMetrics } from "./VRFactory";

const SpeechAceVRFactory = async (audioBlob, data, userInfo, lessonName, recordingAttempts, engagementTime) => {
  const text = data.en; // Phrase from the lesson

  const handleScoreDataSpeechace = async (audioBlob, text) => {
    try {
      await LogProcess("success", `Sending audio data for scoring - SpeechAce`);

      const formData = new FormData();
      const score_type = data.section === "Unlock" ? "scoring/speech" : "scoring/text";

      formData.append("user_id", userInfo.id);
      formData.append("user_name", userInfo.username); // Added user_name
      formData.append("book_name", data.book_name);    // Added book_name from lesson data
      formData.append("section_name", data.section);   // Added section_name from lesson data
      formData.append("exercise_id", String(data.id));
      formData.append("text", text);                   // Added phrase
      formData.append("score_type", score_type);
      formData.append("audio", audioBlob);

      const response = await getScoreDataSpeechace(formData);

      await LogProcess("success", `##### At handleScoreDataSpeechace 
        Received data from getScoreDataSpeechace: ${JSON.stringify(response)}`);

      // Standardize metrics to match expected structure
      if (response && response.data && response.data.status_code === 200) {

        await LogProcess("success", `At handleScoreDataSpeechace response is ${JSON.stringify(response)}`);

        return { success: true, data: response.data };
      } else {
        await LogProcess("error", `SpeechAce returned an error: ${JSON.stringify(response)}`);
        return { success: false, error: response };
      }
    } catch (error) {
      await LogProcess("error", `Error at handleScoreDataSpeechace in SpeechAce API: ${JSON.stringify(error)}`);
      return { success: false, error: error };
    }
  };

  return handleScoreDataSpeechace(audioBlob, text);
};


const getResponseStatus = async (res) => {

  await LogProcess("success", `Starting processment to method getResponseStatus`);

  const hasSuccessfulStatus = res && res.status >= 200 && res.status <= 299;
  const hasScores = res && res.data && Array.isArray(res.data.scores);

  if (hasSuccessfulStatus || hasScores) {
    await LogProcess("success", `Processment result success at method getResponseStatus is ${JSON.stringify(res)}`);
    return 'success';
  }
  if (res && res.response && res.response.status === 404) {
    await LogProcess("warn", `Processment result not_found at method getResponseStatus is ${JSON.stringify(res)}`);
    return 'not_found';
  }

  await LogProcess("error", `Processment result error at method getResponseStatus is ${JSON.stringify(res)}`);
  return 'error';
}

const fetchSpeechAceVRData = async (blockData, currentPhrase, tenant) => {
  try {
    await LogProcess("success", `At fetchSpeechAceVRData is starting working`);
    const userInfo = JSON.parse(localStorage.getItem("user-info"));
    const res = await getMetricsWithFallback(userInfo.id, blockData.block_id, tenant);

    // notFound is the consolidated /v2/progress signal for "no metrics record yet" (replaces the old 404 branch below).
    if (res && res.data && res.data.notFound) {
      await LogProcess("success", "No previous attempts found for user/block (consolidated notFound).");
      return {
        success: true,
        data: { data: { status_code: 200, attempts: 0 } },
        message: 'No previous attempts found'
      };
    }

    const status = await getResponseStatus(res);

    if (status === 'success' && res.data && res.data.scores && Array.isArray(res.data.scores)) {
      // Verifica se há pontuações antes de filtrar
      if (res && res.data && res.data.scores && res.data.scores.length > 0) {

        // Log para verificar os dados scores
        await LogProcess("success", `Filtered scores: ${JSON.stringify(res.data.scores)}`);
        
        // Filtra os dados de acordo com a phrase igual ao currentPhrase
        let filteredScores = [];

        if (Array.isArray(res.data.scores) && res.data.scores.length > 0) {
          filteredScores = res.data.scores.filter(score => {
            if (score && score.phrase && typeof score.phrase === 'string') {
              // Verifica se a frase do score corresponde à frase atual
              return score.phrase.trim() === currentPhrase.trim();
            }
            return false;
          });
        }

        // Check of is an array empty filteredScores array
        if (filteredScores.length === 0) {
          // Trata o caso onde não há score para a frase atual
          await LogProcess("success", "No score was found for current phrase.");

          return {
            success: true,
            data: {
              data: {
                status_code: 200,
                attempts: 0
              }
            }, message: 'No previous attempts found'
          };
        } 

        // Log para verificar os dados filtrados
        await LogProcess("success", `Filtered scores: ${JSON.stringify(filteredScores)}`);

        // Padroniza os dados filtrados, se necessário
        const standardizedData = standardizeVRData({ data: { ...res.data, scores: filteredScores } });

        await LogProcess("success", `Standardized data to return: ${JSON.stringify(standardizedData)}`);
        return { success: true, data: standardizedData };

      } else {
        await LogProcess("warn", `No scores available in the response: ${JSON.stringify(res)}`);
        return { success: false, message: 'No scores available' };
      }
    }

    return { success: false, message: 'No scores available or error fetching attempts' };

  } catch (error) {

    //{"error":"Could not find user with provided \"userId\" and \"blockId\""}
    if (error.message == 'Request failed with status code 404') {
      return {
        success: true,
        data: {
          data: {
            status_code: 200,
            attempts: 0
          }
        }, message: 'No previous attempts found'
      };
    }

    const message = error.message || "";
    await LogProcess("error", `Error at fetchSpeechAceVRData fetching metrics: ${JSON.stringify(message)}`);
    return { success: false, error };
  }
};


const handleSpeechAceVRAttemptMetrics = async (response, metrics, userInfo, data, lessonId, lessonName,
  totalIndexes, blockData, recordingAttempts, engagementTime,
  callbackResponseScore, callbackRegisterMetrics) => {
  await LogProcess("success", `At method handleAttemptMetrics 
    - Starting handle attempt metrics ${JSON.stringify(metrics)}`);

  if (!metrics || !metrics.result) {
    await LogProcess("error", "Error: Invalid metrics data received");
    return recordingAttempts; // No increment in attempts
  }

  await LogProcess("warn", `at handleSpeechAceVRAttemptMetrics ${JSON.stringify(response)} `);

  if (metrics.status_code === 200) {

    // Invoke the callback for updating the score
    callbackResponseScore(response);

    // Update the recording attempts count
    const newRecordingAttempts = recordingAttempts + 1;
    const overall_score = response.data.result.overall_score;

    await sendDataProgressTrackerMetrics(userInfo, lessonId, lessonName, totalIndexes,
      blockData, overall_score, newRecordingAttempts,
      engagementTime, data, callbackRegisterMetrics, "speechace");

    return newRecordingAttempts;
  } else {
    LogProcess("error", `Error: SpeechAce returned a status code different than 200 - ${JSON.stringify(metrics)}`);
    return recordingAttempts; // No increment in attempts
  }
};



export { SpeechAceVRFactory, fetchSpeechAceVRData, handleSpeechAceVRAttemptMetrics }